import React, { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import rawOddsService from '../../utils/services/rawOddsService';
import AFL_PLAYERS from '../../assets/static_data/afl_players.json';
import { BookmakerLogo, BOOKMAKER_DISPLAY_NAMES, bookmakerRank } from '../HotProps/bookmakerAssets';
import PriceMovementsModal from '../OddsScreen/PriceMovementsModal';
import CountdownRing from './CountdownRing';
import SubscriptionRequired from '../common/SubscriptionRequired';

// Auto-refresh cadence (seconds) for live odds.
const REFRESH_SECONDS = 60;

// lowercase player name → static player record (image_src etc.). The raw odds
// feed only carries names, so images are resolved by name.
const NAME_INDEX = new Map(AFL_PLAYERS.map((p) => [p.player_name?.trim().toLowerCase(), p]));

// Same market categories (and order) as the Odds Screen, prefixed with "all".
const MARKET_CHIPS = [
    { value: 'all', label: 'All Markets' },
    { value: 'disposals', label: 'Disposals' },
    { value: 'goals', label: 'Goals' },
    { value: 'marks', label: 'Marks' },
    { value: 'tackles', label: 'Tackles' },
    { value: 'fantasy_points', label: 'Fantasy Points' },
    { value: 'kicks', label: 'Kicks' },
    { value: 'handballs', label: 'Handballs' },
    { value: 'clearances', label: 'Clearances' },
    { value: 'hit_outs', label: 'Hit Outs' },
];

const CATEGORY_ORDER = MARKET_CHIPS.slice(1).map((c) => c.value);
const CATEGORY_LABELS = Object.fromEntries(MARKET_CHIPS.map((c) => [c.value, c.label]));

// Map a specific market line (e.g. "15+ Disposals", "1+ Goal") to its category.
const STAT_TO_CATEGORY = {
    'goal': 'goals',
    'goals': 'goals',
    'disposal': 'disposals',
    'disposals': 'disposals',
    'kick': 'kicks',
    'kicks': 'kicks',
    'handball': 'handballs',
    'handballs': 'handballs',
    'mark': 'marks',
    'marks': 'marks',
    'tackle': 'tackles',
    'tackles': 'tackles',
    'clearance': 'clearances',
    'clearances': 'clearances',
    'hit out': 'hit_outs',
    'hit outs': 'hit_outs',
    'fantasy point': 'fantasy_points',
    'fantasy points': 'fantasy_points',
};

function parseMarketLine(market) {
    const m = /^(\d+(?:\.\d+)?)\+\s+(.+)$/.exec(market || '');
    if (!m) return { threshold: null, category: null };
    return {
        threshold: parseFloat(m[1]),
        category: STAT_TO_CATEGORY[m[2].trim().toLowerCase()] || null,
    };
}

function bookmakerLabel(bk) {
    return BOOKMAKER_DISPLAY_NAMES[bk] || bk;
}

function formatOdds(price) {
    return price != null ? Number(price).toFixed(2) : '—';
}

function isDerivedPrice(entry, bk) {
    return entry?.isDerived?.[bk] === true;
}

// `market_edge` arrives from the backend already expressed as a percentage
// (e.g. 1.26 → +1.3%).
function formatEdgePct(edge) {
    if (edge == null) return '—';
    return `${edge >= 0 ? '+' : ''}${edge.toFixed(1)}%`;
}

// Edge heat — higher edge = brighter green (market edge is always ≥ 0).
function edgeClasses(edge) {
    if (edge == null) return 'text-slate-600';
    if (edge >= 8) return 'text-emerald-300 font-semibold';
    if (edge >= 4) return 'text-emerald-400 font-semibold';
    if (edge > 0) return 'text-emerald-500/90';
    return 'text-slate-500';
}

function formatHitRate(rate) {
    if (rate == null) return '—';
    return `${Math.round(rate * 100)}%`;
}

// Hit-rate heat — higher hit rate = brighter green.
function hitRateClasses(rate) {
    if (rate == null) return 'text-slate-600';
    if (rate >= 0.65) return 'text-emerald-300';
    if (rate >= 0.5) return 'text-emerald-400';
    if (rate >= 0.35) return 'text-emerald-500/90';
    if (rate > 0) return 'text-slate-400';
    return 'text-slate-500';
}

// Same heat as hitRateClasses but as a filled chip (bg + text).
function hitRateChipClasses(rate) {
    if (rate == null) return 'bg-slate-800/60 text-slate-500';
    if (rate >= 0.65) return 'bg-emerald-500/20 text-emerald-300';
    if (rate >= 0.5) return 'bg-emerald-500/15 text-emerald-400';
    if (rate >= 0.35) return 'bg-emerald-500/10 text-emerald-500/90';
    if (rate > 0) return 'bg-slate-700/40 text-slate-300';
    return 'bg-slate-800/60 text-slate-500';
}

// Implied edge (historical hit rate vs the offered price) can go negative, so
// positive values run a green scale (brighter = better) and negatives are red.
function impliedEdgeClasses(v) {
    if (v == null) return 'text-slate-600';
    if (v >= 10) return 'text-emerald-300 font-semibold';
    if (v >= 5) return 'text-emerald-400 font-semibold';
    if (v > 0) return 'text-emerald-500/90';
    if (v < 0) return 'text-rose-400/80';
    return 'text-slate-500';
}

// Same implied-edge heat as a filled chip (bg + text) — used in the matrix.
function impliedEdgeChipClasses(v) {
    if (v == null) return 'bg-slate-800/60 text-slate-500';
    if (v >= 10) return 'bg-emerald-500/25 text-emerald-200';
    if (v >= 5) return 'bg-emerald-500/15 text-emerald-300';
    if (v > 0) return 'bg-emerald-500/10 text-emerald-400/90';
    if (v < 0) return 'bg-rose-500/10 text-rose-400/90';
    return 'bg-slate-800/60 text-slate-500';
}

// Subtle full-cell wash behind matrix cells, tiered by implied edge.
function matrixCellBg(v) {
    if (v == null) return '';
    if (v >= 10) return 'bg-emerald-500/[0.10]';
    if (v >= 5) return 'bg-emerald-500/[0.06]';
    if (v > 0) return 'bg-emerald-500/[0.03]';
    if (v < 0) return 'bg-rose-500/[0.04]';
    return '';
}

// Mini bar chart of a player's recent stat values against the line threshold.
// Bars are chronological (oldest → newest — the API returns most-recent-first
// so we reverse); a bar that cleared the line is emerald, a miss is muted. A
// dashed amber line marks the threshold.
const Sparkline = React.memo(function Sparkline({ stats, threshold, width = 58, height = 22 }) {
    if (!Array.isArray(stats) || stats.length === 0) {
        return <span className="text-[10px] font-mono text-slate-700">—</span>;
    }
    const series = [...stats].reverse();
    const max = Math.max(...series, threshold ?? 0, 1);
    const gap = 2;
    const n = series.length;
    const barW = (width - gap * (n - 1)) / n;
    const scaleY = (v) => (v / max) * height;
    const thresholdY = threshold != null ? height - scaleY(threshold) : null;
    return (
        <svg width={width} height={height} className="block overflow-visible mt-1" aria-hidden="true">
            {series.map((v, i) => {
                const h = Math.max(scaleY(v), 1.5);
                const x = i * (barW + gap);
                const y = height - h;
                const hit = threshold != null && v >= threshold;
                return (
                    <rect
                        key={i}
                        x={x}
                        y={y}
                        width={barW}
                        height={h}
                        rx={1}
                        className={hit ? 'fill-emerald-400/80' : 'fill-slate-600/70'}
                    />
                );
            })}
            {thresholdY != null && (
                <line
                    x1={0}
                    x2={width}
                    y1={thresholdY}
                    y2={thresholdY}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    className="stroke-amber-400/70"
                />
            )}
        </svg>
    );
});

// Shared geometry for the right-pinned summary rail (Avg Market Price + Market
// Edge). Full class strings are written out as literals so Tailwind's JIT can
// see them; the offsets must stay consistent: AVG sits left of EDGE, so its
// right offset equals EDGE's width, and the player-header book icons clear the
// combined width of both.
const AVG_COL = 'w-[100px] min-w-[100px]';
const EDGE_COL = 'w-[88px] min-w-[88px]';
// Sticky only from md up — on mobile the columns scroll normally so the board
// isn't squeezed by pinned rails on a narrow viewport.
const AVG_STICKY = 'md:sticky md:right-[88px]';        // = EDGE width
const EDGE_STICKY = 'md:sticky md:right-0';
const HEADER_BOOKS_OFFSET = 'md:right-[188px]';     // = AVG width + EDGE width

// Per-game board UI state (view mode, market filter, sort, search, collapsed
// groups) kept alive for the SPA session so leaving the page — e.g. jumping
// into the Prop Tool — and coming back restores the view instead of resetting
// to defaults. Keyed by matchId.
const boardStateCache = new Map();

// Bookmaker price cells — shared by the grouped board rows and the flat edge
// rows so both stay column-aligned. The row's best (non-derived) price is
// highlighted; derived prices get the amber badge.
function renderBookCells(entry, bookmakers, bestOdds) {
    return bookmakers.map((bk) => {
        const price = entry.odds?.[bk];
        const derived = price != null && isDerivedPrice(entry, bk);
        const isBest = price != null && price === bestOdds && !derived;
        return (
            <td
                key={bk}
                className={`px-2.5 py-1.5 text-center whitespace-nowrap border-b border-slate-800/40 ${isBest ? 'bg-emerald-500/10' : ''}`}
            >
                {price != null ? (
                    <span className="inline-flex items-center justify-center gap-1">
                        <span className={`text-[12px] font-mono tabular-nums ${
                            isBest ? 'text-emerald-400 font-semibold' : 'text-slate-400'
                        }`}>
                            {formatOdds(price)}
                        </span>
                        {derived && <DerivedBadge />}
                    </span>
                ) : (
                    <span className="text-slate-800 font-mono text-[12px]">—</span>
                )}
            </td>
        );
    });
}

// The right-pinned Avg Market Price + Market Edge cells.
function renderSummaryRail(avgPrice, edge) {
    return (
        <>
            <td className={`${AVG_STICKY} z-10 bg-slate-900 px-2.5 py-1.5 text-center whitespace-nowrap border-b border-l border-slate-800/60 ${AVG_COL}`}>
                <span className="text-[12px] font-mono tabular-nums text-slate-400">
                    {avgPrice != null ? formatOdds(avgPrice) : '—'}
                </span>
            </td>
            <td className={`${EDGE_STICKY} z-10 bg-slate-900 px-2.5 py-1.5 text-center whitespace-nowrap border-b border-slate-800/40 ${EDGE_COL}`}>
                <span className={`text-[12px] font-mono tabular-nums ${edgeClasses(edge)}`}>
                    {formatEdgePct(edge)}
                </span>
            </td>
        </>
    );
}

// Bookmaker column headers (logo + short name) — shared by both table heads.
function renderBookmakerThs(bookmakers) {
    return bookmakers.map((bk) => (
        <th
            key={bk}
            className="px-2.5 py-2 text-center border-b border-slate-800/70 bg-slate-900/40 whitespace-nowrap min-w-[64px]"
        >
            <div className="flex flex-col items-center gap-1">
                <BookmakerLogo bookmaker={bk} size={18} />
                <span className="text-[9px] font-mono text-slate-500">{bookmakerLabel(bk)}</span>
            </div>
        </th>
    ));
}

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Compact avatar resolved by player name, falling back to initials.
function OddsPlayerAvatar({ playerName, size = 24 }) {
    const [errored, setErrored] = useState(false);
    useEffect(() => { setErrored(false); }, [playerName]);

    const record = NAME_INDEX.get(playerName?.trim().toLowerCase());
    const dim = { width: size, height: size };

    if (errored || !record?.image_src) {
        return (
            <div
                style={dim}
                className="rounded-full bg-gradient-to-br from-slate-700 to-slate-800 ring-1 ring-slate-700/60 flex items-center justify-center flex-shrink-0"
            >
                <span className="font-mono font-bold text-emerald-300/90" style={{ fontSize: size * 0.34 }}>
                    {getInitials(playerName)}
                </span>
            </div>
        );
    }

    return (
        <div style={dim} className="rounded-full bg-slate-800 ring-1 ring-slate-700/60 overflow-hidden flex-shrink-0">
            <img
                src={record.image_src}
                alt={playerName}
                width={size}
                height={size}
                loading="lazy"
                onError={() => setErrored(true)}
                className="w-full h-full object-cover object-top"
            />
        </div>
    );
}

// Small square row-action icon button with the app-standard tooltip, matching
// the pattern used across Snipe Feed / Multi Builder / the team grids. The
// tooltip is portaled to <body> so the board's horizontal scroll container
// can't clip it.
function RowIconButton({ tooltip, ariaLabel, accentClasses, icon, onClick }) {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const show = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const maxWidth = 260;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - maxWidth - 8));
        const below = r.bottom + 6;
        const flipUp = below + 36 > window.innerHeight;
        setTip({ left, top: flipUp ? r.top - 6 : below, flipUp, maxWidth });
    }, []);
    const hide = useCallback(() => setTip(null), []);

    const activate = (e) => { e.stopPropagation(); hide(); onClick(); };

    return (
        <span ref={ref} className="inline-flex flex-shrink-0">
            <button
                type="button"
                onClick={activate}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                aria-label={ariaLabel}
                className={`inline-flex items-center justify-center w-5 h-5 rounded border focus:outline-none cursor-pointer transition-all duration-150 ${accentClasses}`}
            >
                {icon}
            </button>
            {tip && createPortal(
                <span
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        left: tip.left,
                        top: tip.top,
                        maxWidth: tip.maxWidth,
                        transform: tip.flipUp ? 'translateY(-100%)' : 'none',
                        zIndex: 9999,
                    }}
                    className="pointer-events-none px-2.5 py-1.5 rounded-lg
                        bg-slate-900 border border-slate-700/60 shadow-xl shadow-black/40
                        whitespace-nowrap normal-case tracking-normal animate-fade-in"
                >
                    <span className="block text-[11px] font-mono font-normal text-slate-200">
                        {tooltip}
                    </span>
                </span>,
                document.body
            )}
        </span>
    );
}

// Deep-link into the Prop Tool (sky search icon).
function PropToolButton({ label, onClick }) {
    return (
        <RowIconButton
            onClick={onClick}
            ariaLabel={`Open Prop Tool - ${label}`}
            tooltip={<>Open Prop Tool <span className="text-slate-500">-</span> {label}</>}
            accentClasses="text-sky-400 border-sky-500/30 hover:bg-sky-500/10 hover:border-sky-500/50 hover:text-sky-300"
            icon={(
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            )}
        />
    );
}

// Open the price movements (fluctuations) modal for a player/market —
// emerald trend icon matching the Odds Screen's "View Flucts" action.
function FluctsButton({ label, onClick }) {
    return (
        <RowIconButton
            onClick={onClick}
            ariaLabel={`View price fluctuations - ${label}`}
            tooltip={<>View Flucts <span className="text-slate-500">-</span> {label}</>}
            accentClasses="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 hover:border-emerald-500/50 hover:text-emerald-300"
            icon={(
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                </svg>
            )}
        />
    );
}

// Amber info badge + tooltip flagging a derived (modelled) price — same
// treatment as the Odds Screen.
function DerivedBadge({ size = 11 }) {
    return (
        <span
            tabIndex={0}
            role="button"
            aria-label="Derived price: more info"
            className="relative group/derived inline-flex items-center align-middle cursor-help text-amber-400 hover:text-amber-300 focus:text-amber-300 focus:outline-none transition-colors"
        >
            <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
            </svg>
            <span
                role="tooltip"
                className="pointer-events-none absolute bottom-full mb-1.5 right-0 z-[100]
                    w-64 max-w-[calc(100vw-1rem)] px-3 py-2.5 rounded-lg
                    bg-slate-900 border border-slate-700/60 shadow-xl shadow-black/40
                    whitespace-normal break-words normal-case tracking-normal
                    opacity-0 translate-y-1
                    group-hover/derived:opacity-100 group-hover/derived:translate-y-0
                    group-focus/derived:opacity-100 group-focus/derived:translate-y-0
                    transition-all duration-150"
            >
                <span className="block text-[10px] font-mono font-semibold uppercase tracking-widest text-amber-400 mb-1.5">
                    Derived Price
                </span>
                <span className="block text-[11px] font-mono font-normal text-slate-300 leading-relaxed">
                    Calculated using the odds currently offered by this bookmaker — not a directly offered price.
                </span>
            </span>
        </span>
    );
}

// ── Memoized row components ──────────────────────────────────────────────
// The full board runs to ~2,000 rows on "all" markets, so rows are memoized:
// toggling one player group, opening a modal, or typing in the search must
// not re-render every other row. Row props are stable (entries/groups come
// from useMemo, callbacks from useCallback), so React.memo actually holds.

// One market line in the grouped board. Avg + Edge are pinned to the right
// as an always-visible summary rail.
const PlayerMarketRow = React.memo(function PlayerMarketRow({ entry, bookmakers, onOpenPropTool, onOpenTrends }) {
    const { bestOdds, bestBookmaker, avgPrice, edge } = entry;

    return (
        <tr className="group hover:bg-slate-800/20 transition-colors">
            <td className="md:sticky md:left-0 z-10 bg-slate-900 px-3 py-1.5 whitespace-nowrap border-b border-slate-800/40">
                <span className="flex items-center gap-1.5 pl-8">
                    <PropToolButton
                        label={`${entry.player} · ${entry.market}`}
                        onClick={() => onOpenPropTool(entry, bestBookmaker)}
                    />
                    {entry.category && (
                        <FluctsButton
                            label={`${entry.player} · ${entry.market}`}
                            onClick={() => onOpenTrends(entry)}
                        />
                    )}
                    <span className="text-[11px] font-mono font-medium text-emerald-400/90 pl-0.5">{entry.market}</span>
                </span>
            </td>
            {renderBookCells(entry, bookmakers, bestOdds)}
            {renderSummaryRail(avgPrice, edge)}
        </tr>
    );
});

// A player's full group: collapsible header row + market line rows. The
// header content is sticky-left so the player stays visible while the odds
// columns scroll horizontally.
const PlayerSection = React.memo(function PlayerSection({ player, rows, expanded, bookmakers, onToggle, onOpenPropTool, onOpenTrends }) {
    // Books that price at least one of this player's shown lines, in the
    // same column order as the board.
    const headerBooks = useMemo(() => {
        const bookSet = new Set();
        rows.forEach((r) => Object.entries(r.odds || {}).forEach(([bk, price]) => {
            if (price != null) bookSet.add(bk);
        }));
        return bookmakers.filter((bk) => bookSet.has(bk));
    }, [rows, bookmakers]);

    return (
        <>
            <tr>
                <td colSpan={bookmakers.length + 3} className="p-0 bg-slate-900/70 border-b border-slate-800/70">
                    <button
                        type="button"
                        onClick={() => onToggle(player)}
                        aria-expanded={expanded}
                        className="w-full text-left hover:bg-slate-800/40 transition-colors"
                    >
                        <div className="flex items-center w-full py-2">
                            {/* Player identity pinned to the left edge */}
                            <span className="md:sticky md:left-0 flex items-center gap-2 pl-3 pr-2">
                                <svg
                                    className={`w-3 h-3 text-slate-500 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                                <OddsPlayerAvatar playerName={player} />
                                <span className="text-[12px] font-semibold text-white truncate">{player}</span>
                                <span className="text-[10px] font-mono text-slate-500">
                                    {rows.length} {rows.length === 1 ? 'line' : 'lines'}
                                </span>
                            </span>
                            {/* Books pinned right, clearing the Avg/Edge summary rail so
                                they align above the book columns. Only shown while the
                                group is collapsed — expanded rows show the prices anyway. */}
                            {!expanded && headerBooks.length > 0 && (
                                <span
                                    className={`ml-auto md:sticky ${HEADER_BOOKS_OFFSET} flex items-center gap-1 pl-2 pr-3 flex-shrink-0`}
                                    title={`${headerBooks.length} bookmakers pricing ${player}`}
                                >
                                    {headerBooks.map((bk) => (
                                        <span key={bk} className="inline-flex opacity-90">
                                            <BookmakerLogo bookmaker={bk} size={15} />
                                        </span>
                                    ))}
                                </span>
                            )}
                        </div>
                    </button>
                </td>
            </tr>
            {expanded && rows.map((entry) => (
                <PlayerMarketRow
                    key={`${entry.player}-${entry.market}`}
                    entry={entry}
                    bookmakers={bookmakers}
                    onOpenPropTool={onOpenPropTool}
                    onOpenTrends={onOpenTrends}
                />
            ))}
        </>
    );
});

// One row of the flat, ranked list. Same columns as the board (every
// bookmaker price + the pinned Avg/Edge rail), but ungrouped so it can be
// sorted by market edge / avg / player across the whole game.
const EdgeViewRow = React.memo(function EdgeViewRow({ entry, bookmakers, onOpenPropTool, onOpenTrends }) {
    const { bestOdds, bestBookmaker, avgPrice, edge, l5, l10, impliedOdds, seasonHitRate, seasonLineHit, seasonTotalGames, impliedEdgeBest, threshold } = entry;
    return (
        <tr className="group hover:bg-slate-800/20 transition-colors">
            <td className="md:sticky md:left-0 z-10 bg-slate-900 px-3 py-1.5 whitespace-nowrap border-b border-slate-800/40 min-w-[220px]">
                <span className="flex items-center gap-2">
                    <OddsPlayerAvatar playerName={entry.player} size={22} />
                    <PropToolButton
                        label={`${entry.player} · ${entry.market}`}
                        onClick={() => onOpenPropTool(entry, bestBookmaker)}
                    />
                    {entry.category && (
                        <FluctsButton
                            label={`${entry.player} · ${entry.market}`}
                            onClick={() => onOpenTrends(entry)}
                        />
                    )}
                    <span className="flex flex-col leading-tight min-w-0">
                        <span className="text-[11px] font-semibold text-white truncate max-w-[150px]">{entry.player}</span>
                        <span className="text-[10px] font-mono text-emerald-400/80">{entry.market}</span>
                    </span>
                </span>
            </td>
            {/* Recent form — L5 sparkline + hit rate, L10 hit rate */}
            <td className="px-2.5 py-1.5 border-b border-l border-slate-800/60 align-middle">
                <div className="flex flex-col items-center gap-0.5">
                    <Sparkline stats={l5?.stats} threshold={threshold} />
                    <span className={`text-[10px] font-mono font-semibold ${hitRateClasses(l5?.hit_rate)}`}>
                        {formatHitRate(l5?.hit_rate)}
                    </span>
                </div>
            </td>
            <td className="px-2.5 py-1.5 text-center whitespace-nowrap border-b border-slate-800/40">
                <span className={`text-[12px] font-mono font-semibold tabular-nums ${hitRateClasses(l10?.hit_rate)}`}>
                    {formatHitRate(l10?.hit_rate)}
                </span>
            </td>
            {/* Season (implied) — fair odds from hit rate (with the hit rate
                itself as a chip beneath) + implied edge of best book */}
            <td className="px-2.5 py-1.5 text-center whitespace-nowrap border-b border-l border-slate-800/60">
                <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[12px] font-mono tabular-nums text-slate-300">
                        {impliedOdds != null ? formatOdds(impliedOdds) : '—'}
                    </span>
                    {seasonHitRate != null && (
                        <span className={`px-1 py-px rounded text-[9px] font-mono font-semibold ${hitRateChipClasses(seasonHitRate)}`}>
                            {formatHitRate(seasonHitRate)}
                            {seasonLineHit != null && seasonTotalGames != null && (
                                <span className="opacity-70"> ({seasonLineHit}/{seasonTotalGames})</span>
                            )}
                        </span>
                    )}
                </div>
            </td>
            <td className="px-2.5 py-1.5 text-center whitespace-nowrap border-b border-slate-800/40">
                <span className={`text-[12px] font-mono tabular-nums font-semibold ${impliedEdgeClasses(impliedEdgeBest)}`}>
                    {formatEdgePct(impliedEdgeBest)}
                </span>
            </td>
            {renderBookCells(entry, bookmakers, bestOdds)}
            {renderSummaryRail(avgPrice, edge)}
        </tr>
    );
});

// Chip-styled trigger that opens a popover menu — same pattern as the Prop
// Tool's filter chips. `children` is a render prop receiving `{ close }` so
// picks can dismiss the menu. Closes on outside click.
function ChipDropdown({ summary, active, panelWidth = 'w-44', children }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-mono font-medium border transition-all ${
                    active
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                }`}
            >
                <span className="truncate max-w-[150px]">{summary}</span>
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className={`absolute z-50 right-0 md:left-0 md:right-auto mt-1.5 ${panelWidth} rounded-xl border border-slate-700/70 bg-slate-900 shadow-2xl shadow-black/60 overflow-hidden`}>
                    {children({ close: () => setOpen(false) })}
                </div>
            )}
        </div>
    );
}

// A single row inside a ChipDropdown menu (tick marks the selection).
function ChipMenuItem({ label, checked, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] transition-colors hover:bg-slate-800/60"
        >
            <span className="w-3.5 flex-shrink-0">
                {checked && (
                    <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                )}
            </span>
            <span className={`flex-1 flex items-center gap-2 truncate font-mono ${checked ? 'text-white' : 'text-slate-300'}`}>{label}</span>
        </button>
    );
}

// One cell of the matrix view: the player's 2026 implied data for a line
// (fair odds + hit rate) with the selected bookmaker's price and the implied
// edge of that price against the fair odds.
const MatrixCell = React.memo(function MatrixCell({ entry, book, onOpenPropTool }) {
    // Derived prices aren't bettable, so the matrix treats them the same as
    // the book not pricing the line at all — an empty cell.
    const price = (entry && book && !isDerivedPrice(entry, book)) ? entry.odds?.[book] : null;

    if (!entry || price == null) {
        return (
            <td className="px-3 py-2.5 text-center border-b border-l border-slate-800/40">
                <span className="text-slate-800 font-mono text-[12px]">—</span>
            </td>
        );
    }
    const impliedEdge = entry.impliedEdges?.[book] ?? null;

    return (
        <td className={`px-3 py-2 text-center whitespace-nowrap border-b border-l border-slate-800/40 transition-colors ${matrixCellBg(impliedEdge)}`}>
            <div className="flex flex-col items-center gap-1">
                <div className="flex items-center justify-center gap-1.5">
                    <PropToolButton
                        label={`${entry.player} · ${entry.market}`}
                        onClick={() => onOpenPropTool(entry, book)}
                    />
                    <span className="text-[13px] font-mono tabular-nums font-semibold text-white">
                        {formatOdds(price)}
                    </span>
                    <span className={`px-1.5 py-px rounded-md text-[10px] font-mono font-semibold tabular-nums ${impliedEdgeChipClasses(impliedEdge)}`}>
                        {formatEdgePct(impliedEdge)}
                    </span>
                </div>
                <span className="text-[9px] font-mono text-slate-600">
                    impl <span className="text-slate-400">{formatOdds(entry.impliedOdds)}</span>
                    {entry.seasonHitRate != null && (
                        <>
                            <span className="text-slate-700"> · </span>
                            <span className={hitRateClasses(entry.seasonHitRate)}>{formatHitRate(entry.seasonHitRate)}</span>
                            {entry.seasonLineHit != null && entry.seasonTotalGames != null && (
                                <span className="text-slate-500"> {entry.seasonLineHit}/{entry.seasonTotalGames}</span>
                            )}
                        </>
                    )}
                </span>
            </div>
        </td>
    );
});

/**
 * Complete odds board for one game: every market line across every bookmaker.
 *
 * Fetches the game's full board once (`market=all`) and drills down entirely
 * client-side via the market chips — instant switching, and only one hit
 * against the raw-odds rate limit (10/min). Rows are grouped per player: each
 * player gets a collapsible header row (expanded by default) with all their
 * market lines beneath it, ordered by category then threshold. The market
 * chips narrow every group to a single category.
 */
function GameMarketOverviewTable({ matchId, playerSearch: playerSearchProp, onPlayerSearchChange, marketCategoryRequest }) {
    const navigate = useNavigate();
    const location = useLocation();

    // Restore any UI state stashed from a previous visit to this game (e.g.
    // before jumping into the Prop Tool), so returning doesn't reset the view.
    const cached = boardStateCache.get(matchId);

    const [oddsData, setOddsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [marketFilter, setMarketFilter] = useState(cached?.marketFilter ?? 'all');
    // 'board' = grouped raw odds per book · 'edge' = flat list ranked by the
    // best price's edge over the market average.
    const [viewMode, setViewMode] = useState(cached?.viewMode ?? 'board');
    // Edge-view sort. Defaults to biggest implied edge first.
    const [sort, setSort] = useState(cached?.sort ?? { key: 'impliedEdge', dir: 'desc' });
    // Edge-view matrix mode: players × lines grid for a single market
    // category, priced against one selected bookmaker.
    const [matrixOn, setMatrixOn] = useState(cached?.matrixOn ?? false);
    const [matrixBookmaker, setMatrixBookmaker] = useState(cached?.matrixBookmaker ?? null);
    // Matrix column sort: { threshold, dir } or null for alphabetical rows.
    const [matrixSort, setMatrixSort] = useState(cached?.matrixSort ?? null);
    // The player search can be driven externally (e.g. a "find markets" jump
    // from the CBA / Ruck / Kick-In tables) or fall back to its own state.
    const [internalSearch, setInternalSearch] = useState(cached?.internalSearch ?? '');
    const playerSearch = playerSearchProp !== undefined ? playerSearchProp : internalSearch;
    const setPlayerSearch = onPlayerSearchChange || setInternalSearch;
    // Players whose group is collapsed. Empty set = everything expanded, so
    // every player's markets show under their header by default.
    const [collapsedPlayers, setCollapsedPlayers] = useState(() => new Set(cached?.collapsedPlayers ?? []));
    // Row whose price movements (flucts) modal is open, or null.
    const [trendsEntry, setTrendsEntry] = useState(null);
    // Seconds until the next automatic odds refresh (drives the wheel timer).
    const [countdown, setCountdown] = useState(REFRESH_SECONDS);
    const countdownRef = useRef(null);
    const refreshRef = useRef(null);

    // Deep-link into the Prop Tool with the player, market and line from the
    // clicked row pre-selected (e.g. "8+ Marks" → marks @ 7.5), defaulting the
    // implied-edge table to the row's best-priced native bookmaker.
    const openPropTool = useCallback((entry, bestBookmaker) => {
        const line = entry.threshold != null ? entry.threshold - 0.5 : null;
        navigate('/prop-tool', {
            state: {
                playerName: entry.player,
                propType: entry.category || null,
                line,
                bookmaker: bestBookmaker || null,
                from: location.pathname,
            },
        });
    }, [navigate, location.pathname]);

    // `background` refreshes hydrate silently: no loading skeleton, and a
    // transient failure keeps the last good board on screen.
    const fetchOdds = useCallback(async ({ background = false } = {}) => {
        if (!matchId) {
            setIsLoading(false);
            return;
        }
        if (!background) {
            setIsLoading(true);
            setError(null);
        }

        const result = await rawOddsService.getGameOdds(matchId, 'all');

        if (result.success) {
            setOddsData(result.data);
            setError(null);
        } else {
            // Stop polling when the failure can't be fixed by retrying.
            if (result.isSubscriptionError || result.isAuthError) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (refreshRef.current) clearInterval(refreshRef.current);
            }
            if (!background) {
                setOddsData(null);
                setError(result);
            }
        }
        setIsLoading(false);
    }, [matchId]);

    // (Re)start the next-refresh wheel + the auto-refresh interval.
    const startTimers = useCallback(() => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshRef.current) clearInterval(refreshRef.current);
        setCountdown(REFRESH_SECONDS);
        countdownRef.current = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? REFRESH_SECONDS : prev - 1));
        }, 1000);
        refreshRef.current = setInterval(() => {
            fetchOdds({ background: true });
        }, REFRESH_SECONDS * 1000);
    }, [fetchOdds]);

    useEffect(() => {
        fetchOdds();
        startTimers();
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (refreshRef.current) clearInterval(refreshRef.current);
        };
    }, [fetchOdds, startTimers]);

    const handleRefresh = useCallback(() => {
        fetchOdds();
        startTimers();
    }, [fetchOdds, startTimers]);

    // External market-chip request (e.g. a "find markets" jump from the price
    // movement feed carries the movement's category). `ts` makes repeat jumps
    // to the same category re-apply after the user changed the chip manually.
    useEffect(() => {
        const category = marketCategoryRequest?.category;
        if (category && MARKET_CHIPS.some((c) => c.value === category)) {
            setMarketFilter(category);
        }
    }, [marketCategoryRequest]);

    // Stash the current view so returning to this game (e.g. after a Prop Tool
    // detour) restores the same mode, filter, sort and collapsed groups.
    useEffect(() => {
        if (!matchId) return;
        boardStateCache.set(matchId, {
            marketFilter,
            viewMode,
            sort,
            matrixOn,
            matrixBookmaker,
            matrixSort,
            internalSearch,
            collapsedPlayers: [...collapsedPlayers],
        });
    }, [matchId, marketFilter, viewMode, sort, matrixOn, matrixBookmaker, matrixSort, internalSearch, collapsedPlayers]);

    // Enrich each entry with its parsed category/threshold, and lift the
    // backend-computed market metrics (best / avg / edge) and historical form
    // (season implied odds/edge + L5/L10 windows) to the top level.
    const entries = useMemo(() => {
        return (oddsData?.odds || []).map((e) => {
            const { threshold, category } = parseMarketLine(e.market);
            const bestBookmaker = e.edge?.best_bookmaker ?? null;

            // The season window is the numeric-year key (e.g. "2026"),
            // distinct from the fixed last_5 / last_10 windows.
            const hist = e.historical_data || {};
            const seasonKey = Object.keys(hist).find(
                (k) => k !== 'last_5_games' && k !== 'last_10_games'
            );
            const season = seasonKey ? hist[seasonKey] : null;

            return {
                ...e,
                threshold,
                category,
                bestOdds: e.edge?.best_odds ?? null,
                bestBookmaker,
                avgPrice: e.edge?.avg_market_price ?? null,
                edge: e.edge?.market_edge ?? null,
                bookCount: Object.keys(e.odds || {}).length,
                // Historical form
                l5: hist.last_5_games || null,
                l10: hist.last_10_games || null,
                impliedOdds: season?.implied_odds ?? null,
                seasonHitRate: season?.hit_rate ?? null,
                seasonLineHit: season?.line_hit ?? null,
                seasonTotalGames: season?.total_games ?? null,
                impliedEdges: season?.implied_edges ?? null,
                impliedEdgeBest: (season?.implied_edges && bestBookmaker)
                    ? (season.implied_edges[bestBookmaker] ?? null)
                    : null,
            };
        });
    }, [oddsData]);

    // Union of bookmakers across the whole board — stable columns regardless
    // of the active drilldown, in the canonical bookmaker order (unknown
    // books after, alphabetically).
    const bookmakers = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => Object.keys(e.odds || {}).forEach((bk) => set.add(bk)));
        return [...set].sort((a, b) =>
            bookmakerRank(a) - bookmakerRank(b) || bookmakerLabel(a).localeCompare(bookmakerLabel(b))
        );
    }, [entries]);

    // Defer the search term for filtering so keystrokes stay responsive —
    // the input updates immediately while the (large) table re-render runs
    // at background priority.
    const deferredSearch = useDeferredValue(playerSearch);

    // Entries narrowed to the active market chip + player search.
    const filteredEntries = useMemo(() => {
        let list = entries;
        if (marketFilter !== 'all') {
            list = list.filter((e) => e.category === marketFilter);
        }
        const q = deferredSearch.trim().toLowerCase();
        if (q) {
            list = list.filter((e) => (e.player || '').toLowerCase().includes(q));
        }
        return list;
    }, [entries, marketFilter, deferredSearch]);

    // Rows grouped per player (alphabetical), each player's lines ordered by
    // market category (canonical chip order) then ascending threshold.
    const playerGroups = useMemo(() => {
        const map = new Map();
        filteredEntries.forEach((e) => {
            const key = e.player || 'Unknown';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(e);
        });
        const catRank = (c) => {
            const i = CATEGORY_ORDER.indexOf(c);
            return i === -1 ? CATEGORY_ORDER.length : i;
        };
        map.forEach((rows) => {
            rows.sort((a, b) => {
                const c = catRank(a.category) - catRank(b.category);
                if (c !== 0) return c;
                return (a.threshold ?? 0) - (b.threshold ?? 0);
            });
        });
        return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [filteredEntries]);

    // Start every player group collapsed by default so the board opens as a
    // compact list of players rather than a wall of rows. Runs once after the
    // first load and only when there's no restored state to honour (returning
    // from the Prop Tool keeps whatever the user had).
    //
    // `hadRestoredState` is snapshotted at mount because the persistence effect
    // below writes an entry (with an empty collapsedPlayers array) on mount, so
    // the live `cached` const can't be trusted to distinguish a fresh load from
    // a genuine return visit by the time the data arrives.
    const defaultCollapseApplied = useRef(false);
    const hadRestoredState = useRef(Boolean(cached?.collapsedPlayers));
    useEffect(() => {
        if (defaultCollapseApplied.current) return;
        if (hadRestoredState.current) { defaultCollapseApplied.current = true; return; }
        if (playerGroups.length === 0) return;
        setCollapsedPlayers(new Set(playerGroups.map(([p]) => p)));
        defaultCollapseApplied.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerGroups]);

    // Flat, ranked list for the Edge view — every line as its own row, sorted
    // by the active column. Lines with no comparable price (null edge) always
    // sink to the bottom regardless of direction.
    const edgeRows = useMemo(() => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        const val = (e) => {
            switch (sort.key) {
                case 'best': return e.bestOdds;
                case 'avg': return e.avgPrice;
                case 'l5': return e.l5?.hit_rate ?? null;
                case 'l10': return e.l10?.hit_rate ?? null;
                case 'impliedOdds': return e.impliedOdds;
                case 'impliedEdge': return e.impliedEdgeBest;
                default: return e.edge;
            }
        };
        return [...filteredEntries].sort((a, b) => {
            if (sort.key === 'player') {
                const p = (a.player || '').localeCompare(b.player || '');
                if (p !== 0) return dir * p;
                return (a.threshold ?? 0) - (b.threshold ?? 0);
            }
            const av = val(a);
            const bv = val(b);
            const an = av == null;
            const bn = bv == null;
            if (an && bn) return 0;
            if (an) return 1;
            if (bn) return -1;
            return dir * (av - bv);
        });
    }, [filteredEntries, sort]);

    const matrixActive = viewMode === 'edge' && matrixOn;

    // The matrix pivots one market category into columns, so "all" isn't a
    // valid drilldown while it's on. The category is derived in render (falls
    // back to the first category with lines) so the grid never shows a
    // mixed-category frame; the effect below just snaps the chip UI to match.
    const matrixCategory = useMemo(() => {
        if (!matrixActive) return null;
        if (marketFilter !== 'all') return marketFilter;
        return CATEGORY_ORDER.find((c) => entries.some((e) => e.category === c)) || CATEGORY_ORDER[0];
    }, [matrixActive, marketFilter, entries]);

    useEffect(() => {
        if (matrixActive && marketFilter === 'all' && matrixCategory) {
            setMarketFilter(matrixCategory);
        }
    }, [matrixActive, marketFilter, matrixCategory]);

    // Entries feeding the matrix — the active category plus the player search.
    const matrixEntries = useMemo(() => {
        if (!matrixActive) return [];
        return marketFilter === matrixCategory
            ? filteredEntries
            : filteredEntries.filter((e) => e.category === matrixCategory);
    }, [matrixActive, marketFilter, matrixCategory, filteredEntries]);

    // Players × lines grid for the active category: column per threshold,
    // row per player, cell = that player's entry for the line (or null).
    const matrix = useMemo(() => {
        if (!matrixActive) return null;
        const thresholds = [...new Set(
            matrixEntries.map((e) => e.threshold).filter((t) => t != null)
        )].sort((a, b) => a - b);
        const byPlayer = new Map();
        matrixEntries.forEach((e) => {
            if (e.threshold == null) return;
            const key = e.player || 'Unknown';
            if (!byPlayer.has(key)) byPlayer.set(key, new Map());
            byPlayer.get(key).set(e.threshold, e);
        });
        const players = [...byPlayer.keys()].sort((a, b) => a.localeCompare(b));
        return { thresholds, players, byPlayer };
    }, [matrixActive, matrixEntries]);

    // Books with at least one native (non-derived) price in the active
    // category — the chip list users switch between in matrix mode. Derived
    // prices are hidden in the grid, so derived-only books are skipped too.
    const matrixBooks = useMemo(() => {
        if (!matrixActive) return [];
        const set = new Set();
        matrixEntries.forEach((e) => Object.entries(e.odds || {}).forEach(([bk, price]) => {
            if (price != null && !isDerivedPrice(e, bk)) set.add(bk);
        }));
        return bookmakers.filter((bk) => set.has(bk));
    }, [matrixActive, matrixEntries, bookmakers]);

    // Selected book, falling back to the first available when the stored
    // selection isn't pricing this category.
    const activeMatrixBook = (matrixBookmaker && matrixBooks.includes(matrixBookmaker))
        ? matrixBookmaker
        : (matrixBooks[0] ?? null);

    // Only show line columns the selected book actually prices (natively) for
    // at least one player — books rarely cover every threshold.
    const matrixThresholds = useMemo(() => {
        if (!matrix) return [];
        if (!activeMatrixBook) return matrix.thresholds;
        return matrix.thresholds.filter((t) =>
            matrix.players.some((p) => {
                const e = matrix.byPlayer.get(p).get(t);
                return e && e.odds?.[activeMatrixBook] != null && !isDerivedPrice(e, activeMatrixBook);
            })
        );
    }, [matrix, activeMatrixBook]);

    // Row order: alphabetical by default, or ranked by a clicked line column's
    // implied edge. Players without a bettable price in that column sink to
    // the bottom regardless of direction.
    const matrixPlayers = useMemo(() => {
        if (!matrix) return [];
        if (!matrixSort || !activeMatrixBook || !matrixThresholds.includes(matrixSort.threshold)) {
            return matrix.players;
        }
        const d = matrixSort.dir === 'asc' ? 1 : -1;
        const edgeAt = (p) => {
            const e = matrix.byPlayer.get(p).get(matrixSort.threshold);
            if (!e) return null;
            const price = e.odds?.[activeMatrixBook];
            if (price == null || isDerivedPrice(e, activeMatrixBook)) return null;
            return e.impliedEdges?.[activeMatrixBook] ?? null;
        };
        return [...matrix.players].sort((a, b) => {
            const av = edgeAt(a);
            const bv = edgeAt(b);
            if (av == null && bv == null) return a.localeCompare(b);
            if (av == null) return 1;
            if (bv == null) return -1;
            return d * (av - bv) || a.localeCompare(b);
        });
    }, [matrix, matrixSort, activeMatrixBook, matrixThresholds]);

    const toggleMatrixSort = useCallback((threshold) => {
        setMatrixSort((prev) => (
            prev?.threshold === threshold
                ? { threshold, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
                : { threshold, dir: 'desc' }
        ));
    }, []);

    const toggleSort = (key) => {
        setSort((prev) => (
            prev.key === key
                ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
                : { key, dir: key === 'player' ? 'asc' : 'desc' }
        ));
    };

    const playerCount = useMemo(() => new Set(entries.map((e) => e.player)).size, [entries]);

    // Stable identity so memoized PlayerSections don't re-render on toggle.
    const togglePlayer = useCallback((player) => {
        setCollapsedPlayers((prev) => {
            const next = new Set(prev);
            if (next.has(player)) next.delete(player);
            else next.add(player);
            return next;
        });
    }, []);

    const allCollapsed = playerGroups.length > 0 && playerGroups.every(([p]) => collapsedPlayers.has(p));

    const toggleAll = () => {
        if (allCollapsed) setCollapsedPlayers(new Set());
        else setCollapsedPlayers(new Set(playerGroups.map(([p]) => p)));
    };

    const tableHead = (
        <thead>
            <tr>
                <th className="md:sticky md:left-0 z-10 bg-slate-900 text-left text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 font-medium px-3 py-2.5 border-b border-slate-800/70 min-w-[170px]">
                    Player / Market
                </th>
                {renderBookmakerThs(bookmakers)}
                <th className={`${AVG_STICKY} z-10 bg-slate-900 px-2.5 py-2 text-center text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 font-medium border-b border-l border-slate-800/70 ${AVG_COL}`}>
                    Avg Market Price
                </th>
                <th className={`${EDGE_STICKY} z-10 bg-slate-900 px-2.5 py-2 text-center text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 font-medium border-b border-slate-800/70 ${EDGE_COL}`}>
                    Market Edge
                </th>
            </tr>
        </thead>
    );

    // Column header for the Edge view. Sortable columns render a clickable
    // button with a direction caret; when `sortable` is false it's a plain,
    // non-interactive label (used for Impl. Odds and Avg Market Price).
    // `className` carries sticky/width geometry for pinned cols.
    const sortableTh = (label, key, { align = 'center', className = '', sortable = true } = {}) => {
        const active = sortable && sort.key === key;
        return (
            <th
                className={`bg-slate-900 px-3 py-2.5 border-b border-slate-800/70 ${align === 'left' ? 'text-left' : 'text-center'} ${className}`}
            >
                {sortable ? (
                    <button
                        type="button"
                        onClick={() => toggleSort(key)}
                        className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] font-medium transition-colors ${align === 'center' ? 'justify-center' : ''} ${
                            active ? 'text-emerald-300' : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        {label}
                        <svg
                            className={`w-2.5 h-2.5 flex-shrink-0 transition-transform ${active ? 'opacity-100' : 'opacity-30'} ${active && sort.dir === 'asc' ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                ) : (
                    <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-[0.12em] font-medium text-slate-500 ${align === 'center' ? 'justify-center' : ''}`}>
                        {label}
                    </span>
                )}
            </th>
        );
    };

    return (
        <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
            {/* Header — full toolbar on desktop, compact two-row layout on mobile */}
            <div className="mb-3">
            {/* Desktop toolbar */}
            <div className="hidden md:flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex items-center gap-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Game Market Overview</span>
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600">every line · every book</span>
                </div>
                <div className="flex items-center gap-2.5">
                    {!isLoading && !error && entries.length > 0 && (
                        <span className="hidden lg:inline text-[10px] font-mono text-slate-500">
                            <span className="text-white font-semibold">{entries.length}</span> lines
                            <span className="text-slate-700"> · </span>
                            <span className="text-white font-semibold">{playerCount}</span> players
                            <span className="text-slate-700"> · </span>
                            <span className="text-white font-semibold">{bookmakers.length}</span> books
                        </span>
                    )}
                    {/* View mode: grouped board vs edge-ranked list */}
                    {!isLoading && !error && entries.length > 0 && (
                        <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-950/60 border border-slate-800/70">
                            {[{ id: 'board', label: 'All Odds' }, { id: 'edge', label: 'Edge' }].map((v) => {
                                const active = viewMode === v.id;
                                return (
                                    <button
                                        key={v.id}
                                        type="button"
                                        onClick={() => setViewMode(v.id)}
                                        className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-semibold transition-all ${
                                            active
                                                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                                : 'text-slate-400 border border-transparent hover:text-slate-200'
                                        }`}
                                    >
                                        {v.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {/* Matrix toggle — pivots the edge list into a players × lines grid */}
                    {!isLoading && !error && entries.length > 0 && viewMode === 'edge' && (
                        <button
                            type="button"
                            onClick={() => setMatrixOn((v) => !v)}
                            aria-pressed={matrixOn}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-mono font-semibold border transition-all ${
                                matrixOn
                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                    : 'text-slate-400 border-slate-700/70 hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600'
                            }`}
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                            </svg>
                            Implied Matrix
                        </button>
                    )}
                    {!isLoading && !error && entries.length > 0 && (
                        <div className="relative">
                            <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search player..."
                                value={playerSearch}
                                onChange={(e) => setPlayerSearch(e.target.value)}
                                className="pl-7 pr-6 py-1 w-36 md:w-48 bg-slate-900/50 border border-slate-700/70 rounded-lg text-[11px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/60 transition-colors"
                            />
                            {playerSearch && (
                                <button
                                    type="button"
                                    onClick={() => setPlayerSearch('')}
                                    aria-label="Clear player search"
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    )}
                    {!isLoading && !error && viewMode === 'board' && playerGroups.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono font-medium text-slate-400 border border-slate-700/70 rounded-lg hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all"
                        >
                            {allCollapsed ? 'Expand all' : 'Collapse all'}
                        </button>
                    )}
                    {!isLoading && <CountdownRing countdown={countdown} total={REFRESH_SECONDS} />}
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono font-medium text-slate-400 border border-slate-700/70 rounded-lg hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50"
                    >
                        <svg className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Mobile toolbar — view toggle row, then search + timer row */}
            {!isLoading && !error && entries.length > 0 && (
                <div className="flex flex-col gap-2 md:hidden">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Game Market Overview</span>
                    {/* Row 1: All Odds / Edge (50/50), plus Implied Matrix (→ 33/33/33) in edge view */}
                    <div className={`grid gap-1.5 ${viewMode === 'edge' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        {[{ id: 'board', label: 'All Odds' }, { id: 'edge', label: 'Edge' }].map((v) => {
                            const active = viewMode === v.id;
                            return (
                                <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => setViewMode(v.id)}
                                    className={`w-full px-2 py-1.5 rounded-lg text-[11px] font-mono font-semibold border text-center transition-all ${
                                        active
                                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                            : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200'
                                    }`}
                                >
                                    {v.label}
                                </button>
                            );
                        })}
                        {viewMode === 'edge' && (
                            <button
                                type="button"
                                onClick={() => setMatrixOn((v) => !v)}
                                aria-pressed={matrixOn}
                                className={`flex w-full items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-mono font-semibold border transition-all ${
                                    matrixOn
                                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                        : 'text-slate-400 border-slate-700/70 hover:bg-slate-800/60 hover:text-slate-200'
                                }`}
                            >
                                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                                </svg>
                                Implied Matrix
                            </button>
                        )}
                    </div>
                    {/* Row 2: player search (80%) + refresh timer (20%) */}
                    <div className="flex items-center gap-2">
                        <div className="relative flex-[4]">
                            <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search player..."
                                value={playerSearch}
                                onChange={(e) => setPlayerSearch(e.target.value)}
                                className="w-full pl-7 pr-6 py-1.5 bg-slate-900/50 border border-slate-700/70 rounded-lg text-[11px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/60 transition-colors"
                            />
                            {playerSearch && (
                                <button
                                    type="button"
                                    onClick={() => setPlayerSearch('')}
                                    aria-label="Clear player search"
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <div className="flex shrink-0 items-center justify-center">
                            <CountdownRing countdown={countdown} total={REFRESH_SECONDS} />
                        </div>
                    </div>
                </div>
            )}
            </div>

            {/* Market drilldown chips */}
            <div
                className="flex items-center gap-1.5 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible mb-3 -mx-1 px-1 md:mx-0 md:px-0"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {MARKET_CHIPS.filter((chip) => !(matrixActive && chip.value === 'all')).map((chip) => {
                    const active = chip.value === marketFilter;
                    return (
                        <button
                            key={chip.value}
                            type="button"
                            onClick={() => setMarketFilter(chip.value)}
                            className={`flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-mono font-medium border transition-all ${
                                active
                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                    : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                            }`}
                        >
                            {chip.label}
                        </button>
                    );
                })}
                {/* Matrix bookmaker selector — which book's odds the implied
                    edge is measured against */}
                {matrixActive && !isLoading && !error && matrixBooks.length > 0 && (
                    <div className="flex-shrink-0 ml-auto pl-1.5">
                        <ChipDropdown
                            active
                            panelWidth="w-48"
                            summary={(
                                <span className="flex items-center gap-1.5">
                                    {activeMatrixBook && <BookmakerLogo bookmaker={activeMatrixBook} size={14} />}
                                    {activeMatrixBook ? bookmakerLabel(activeMatrixBook) : 'Bookmaker'}
                                </span>
                            )}
                        >
                            {({ close }) => matrixBooks.map((bk) => (
                                <ChipMenuItem
                                    key={bk}
                                    checked={bk === activeMatrixBook}
                                    onClick={() => { setMatrixBookmaker(bk); close(); }}
                                    label={(
                                        <>
                                            <BookmakerLogo bookmaker={bk} size={14} />
                                            {bookmakerLabel(bk)}
                                        </>
                                    )}
                                />
                            ))}
                        </ChipDropdown>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex items-center gap-2.5 py-10 justify-center">
                    <div className="w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
                    <span className="text-xs font-mono text-slate-500">Loading game odds…</span>
                </div>
            ) : error ? (
                error.isSubscriptionError ? (
                    <SubscriptionRequired feature="raw odds data" variant="inline" />
                ) : (
                    <div className="py-10 flex flex-col items-center text-center gap-3">
                        <span className="text-xs font-mono text-slate-500 max-w-sm leading-relaxed">
                            {error.message || 'Unable to load odds for this game.'}
                        </span>
                        {['RATE_LIMIT_EXCEEDED', 'NETWORK_ERROR', 'SERVER_ERROR', 'SERVICE_UNAVAILABLE', 'NO_DATA', 'SUBSCRIPTION_CHECK_FAILED'].includes(error.errorCode) && (
                            <button
                                type="button"
                                onClick={handleRefresh}
                                className="px-3 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                            >
                                Retry
                            </button>
                        )}
                    </div>
                )
            ) : entries.length === 0 ? (
                <div className="py-10 text-center text-xs font-mono text-slate-500">
                    No priced markets for this game yet. Odds will appear once bookmakers open their lines.
                </div>
            ) : filteredEntries.length === 0 ? (
                <div className="py-10 text-center text-xs font-mono text-slate-500">
                    {playerSearch.trim()
                        ? <>No players matching "{playerSearch.trim()}"{marketFilter !== 'all' ? ` with ${CATEGORY_LABELS[marketFilter]} lines` : ''}.</>
                        : <>No {CATEGORY_LABELS[marketFilter] || 'matching'} lines currently priced for this game.</>}
                </div>
            ) : matrixActive ? (
                // ── Matrix view: players × lines grid for one category, with
                // the implied edge of the selected book's price per cell ──
                matrix && matrixThresholds.length > 0 ? (
                    <div className="overflow-x-auto styled-scrollbar rounded-xl border border-slate-800/70">
                        <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    <th className="md:sticky md:left-0 z-10 bg-slate-900 text-left text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 font-medium px-3 py-2.5 border-b border-slate-800/70 min-w-[200px]">
                                        Player
                                    </th>
                                    {matrixThresholds.map((t) => {
                                        const sortActive = matrixSort?.threshold === t;
                                        return (
                                            <th
                                                key={t}
                                                className="px-3 py-2.5 text-center border-b border-l border-slate-800/70 bg-slate-900/40 whitespace-nowrap min-w-[112px]"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => toggleMatrixSort(t)}
                                                    title={`Sort players by implied edge at ${t}+`}
                                                    className="inline-flex items-center justify-center gap-1.5 transition-colors"
                                                >
                                                    {activeMatrixBook && <BookmakerLogo bookmaker={activeMatrixBook} size={16} />}
                                                    <span className={`text-[13px] font-mono font-bold leading-none ${sortActive ? 'text-emerald-200' : 'text-emerald-300'}`}>
                                                        {t}+
                                                    </span>
                                                    <svg
                                                        className={`w-2.5 h-2.5 flex-shrink-0 transition-transform ${sortActive ? 'opacity-100 text-emerald-200' : 'opacity-30 text-slate-400'} ${sortActive && matrixSort.dir === 'asc' ? 'rotate-180' : ''}`}
                                                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {matrixPlayers.map((player, idx) => {
                                    const lines = matrix.byPlayer.get(player);
                                    // Any of the player's entries works for the flucts
                                    // modal — it targets player + market category.
                                    const fluctsEntry = lines.values().next().value || null;
                                    return (
                                        <tr
                                            key={player}
                                            className={`group hover:bg-slate-800/25 transition-colors ${idx % 2 === 1 ? 'bg-slate-950/30' : ''}`}
                                        >
                                            <td className="md:sticky md:left-0 z-10 bg-slate-900 px-3 py-2 whitespace-nowrap border-b border-slate-800/40">
                                                <span className="flex items-center gap-2.5">
                                                    <OddsPlayerAvatar playerName={player} size={26} />
                                                    {fluctsEntry?.category && (
                                                        <FluctsButton
                                                            label={`${player} · ${CATEGORY_LABELS[matrixCategory]}`}
                                                            onClick={() => setTrendsEntry(fluctsEntry)}
                                                        />
                                                    )}
                                                    <span className="flex flex-col leading-tight min-w-0">
                                                        <span className="text-[12px] font-semibold text-white truncate max-w-[150px]">{player}</span>
                                                        <span className="text-[9px] font-mono text-slate-500">
                                                            Market: <span className="text-emerald-400/80">{CATEGORY_LABELS[matrixCategory]}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </td>
                                            {matrixThresholds.map((t) => (
                                                <MatrixCell
                                                    key={t}
                                                    entry={lines.get(t) || null}
                                                    book={activeMatrixBook}
                                                    onOpenPropTool={openPropTool}
                                                />
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="py-10 text-center text-xs font-mono text-slate-500">
                        No {CATEGORY_LABELS[matrixCategory] || 'matching'} lines currently priced for this game.
                    </div>
                )
            ) : viewMode === 'edge' ? (
                // ── Edge view: flat, sortable board ranked by market edge ──
                <div className="overflow-x-auto styled-scrollbar rounded-xl border border-slate-800/70">
                    <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                        <thead>
                            <tr>
                                {sortableTh('Player / Market', 'player', { align: 'left', className: 'md:sticky md:left-0 z-10 min-w-[220px]' })}
                                {sortableTh('L5', 'l5', { className: 'border-l border-slate-800/60 min-w-[70px]' })}
                                {sortableTh('L10', 'l10', { className: 'min-w-[52px]' })}
                                {sortableTh('Impl. Odds', 'impliedOdds', { className: 'border-l border-slate-800/60 min-w-[62px]', sortable: false })}
                                {sortableTh('Impl. Edge', 'impliedEdge', { className: 'min-w-[64px]' })}
                                {renderBookmakerThs(bookmakers)}
                                {sortableTh('Avg Market Price', 'avg', { className: `${AVG_STICKY} z-10 border-l border-slate-800/70 ${AVG_COL}`, sortable: false })}
                                {sortableTh('Market Edge', 'edge', { className: `${EDGE_STICKY} z-10 ${EDGE_COL}` })}
                            </tr>
                        </thead>
                        <tbody>
                            {edgeRows.map((entry) => (
                                <EdgeViewRow
                                    key={`${entry.player}-${entry.market}`}
                                    entry={entry}
                                    bookmakers={bookmakers}
                                    onOpenPropTool={openPropTool}
                                    onOpenTrends={setTrendsEntry}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                // ── Player-grouped board: each player's markets under their header ──
                <div className="overflow-x-auto styled-scrollbar rounded-xl border border-slate-800/70">
                    <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                        {tableHead}
                        <tbody>
                            {playerGroups.map(([player, rows]) => (
                                <PlayerSection
                                    key={player}
                                    player={player}
                                    rows={rows}
                                    expanded={!collapsedPlayers.has(player)}
                                    bookmakers={bookmakers}
                                    onToggle={togglePlayer}
                                    onOpenPropTool={openPropTool}
                                    onOpenTrends={setTrendsEntry}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Legend */}
            {!isLoading && !error && entries.length > 0 && (
                <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-0.5">
                    {!matrixActive && (
                        <>
                            <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                                <span className="w-3 h-3 rounded bg-emerald-500/25 border border-emerald-500/30" /> best price
                            </span>
                            <span className="text-[10px] font-mono text-slate-500">
                                <span className="text-slate-400 font-semibold">Market Edge</span> = best price vs market avg
                            </span>
                        </>
                    )}
                    {viewMode === 'edge' && !matrixActive && (
                        <span className="text-[10px] font-mono text-slate-500">
                            <span className="text-slate-400 font-semibold">Impl. Odds</span> = fair odds from the player's 2026 hit rate
                        </span>
                    )}
                    {matrixActive && (
                        <>
                            <span className="text-[10px] font-mono text-slate-500">
                                cell = <span className="text-slate-400 font-semibold">{activeMatrixBook ? bookmakerLabel(activeMatrixBook) : 'book'} price</span> + implied edge vs the player's 2026 fair odds
                            </span>
                            <span className="text-[10px] font-mono text-slate-500">
                                <span className="text-slate-400 font-semibold">impl</span> = fair odds · 2026 hit rate
                            </span>
                        </>
                    )}
                    <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                        <svg width={11} height={11} viewBox="0 0 20 20" fill="currentColor" className="text-amber-400">
                            <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
                        </svg>
                        derived price
                    </span>
                    <span className="text-[10px] font-mono text-slate-600">— = not priced by that book</span>
                </div>
            )}

            {/* Price movements (flucts) modal — same experience as the Odds
                Screen, opened per player/market row. `propType` follows the
                clicked row's category and the modal's own prop-type selector
                just re-targets the open row (it doesn't touch the board). */}
            <PriceMovementsModal
                open={Boolean(trendsEntry)}
                player={trendsEntry?.player}
                propType={trendsEntry?.category}
                initialMarket={trendsEntry?.market}
                matchLabel={trendsEntry?.match || oddsData?.match}
                startTime={trendsEntry?.startTime}
                sport="afl"
                onClose={() => setTrendsEntry(null)}
                onPropTypeChange={(next) => {
                    setTrendsEntry((prev) => (prev ? { ...prev, category: next, market: null } : prev));
                }}
            />
        </section>
    );
}

export default GameMarketOverviewTable;
